/**
 * One intake company slot's link (SA-1456 spec §7.5): the linked company as a chip, or "Not linked"
 * with the server's suggestion ("Link to SHEIN?", one click links), and a way to pick another company
 * through `CompanyInput` in pick-only mode. The slot's text is the intake form's alone: linking never
 * changes it (the server keeps it as a spelling of the company when it is not already one of its
 * names). Nothing links without a person's pick. The controls need `inbox:write`; without it the row
 * only reads.
 */
import { useState } from 'react'
import { X } from 'lucide-react'
import { CompanyInput } from '@/components/companies/CompanyInput'
import { NOT_LINKED, linkSuggestion } from '@/components/companies/companyText'
import { Button, Tip } from '@/components/ui'
import type { IntakeLink } from '@/types/commerce'
import type { CompanyLink } from '@/types/companies'
import {
  CANCEL_PICK, CHANGE_COMPANY, LINK_FAILED, PICK_COMPANY, changeFor, clearFor, pickFor, pickerLabel,
} from './intakeLinkText'

export function IntakeCompanyLink({ label, text, link, canWrite, onLink }: {
  /** The intake field's label ("Requesting company"). */
  label: string
  /** The slot's text as stored. */
  text: string
  link: IntakeLink
  canWrite: boolean
  /** Link (`companyId`) or clear (`null`); rejects with the server's sentence. */
  onLink: (companyId: string | null) => Promise<void>
}) {
  const [picking, setPicking] = useState(false)
  const [typed, setTyped] = useState<CompanyLink>({ text, companyId: null })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (companyId: string | null) => {
    if (busy) return
    setBusy(true); setError(null)
    try {
      await onLink(companyId)
      setPicking(false)
    } catch (e) {
      setError((e as Error).message || LINK_FAILED)
    } finally {
      setBusy(false)
    }
  }

  const company = link.company
  if (!company && !text.trim()) return null

  return (
    <div className="mt-1 space-y-1 text-xs" data-testid="intake-company-link">
      {picking ? (
        <div className="space-y-1">
          <CompanyInput label={pickerLabel(label)} pickOnly disabled={busy} suggestion={link.suggestion}
            value={typed}
            onChange={(next) => {
              if (next.companyId) void run(next.companyId)
              else setTyped(next)
            }} />
          <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={busy}
            onClick={() => { setPicking(false); setError(null) }}>{CANCEL_PICK}</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
          {company ? (
            <span data-testid="intake-company-chip"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-foreground">
              <span>{company.name}</span>
              {canWrite && (
                <Tip text={clearFor(label)}>
                  <Button type="button" variant="ghost" size="sm" aria-label={clearFor(label)} disabled={busy}
                    className="h-5 w-5 p-0 text-muted-foreground" onClick={() => void run(null)}>
                    <X className="size-3.5" aria-hidden="true" />
                  </Button>
                </Tip>
              )}
            </span>
          ) : (
            <>
              <span>{NOT_LINKED}</span>
              {canWrite && link.suggestion && (
                <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" disabled={busy}
                  onClick={() => void run(link.suggestion!.id)}>
                  {linkSuggestion(link.suggestion.name)}
                </Button>
              )}
            </>
          )}
          {canWrite && (
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={busy}
              aria-label={company ? changeFor(label) : pickFor(label)}
              onClick={() => { setTyped({ text, companyId: null }); setError(null); setPicking(true) }}>
              {company ? CHANGE_COMPANY : PICK_COMPANY}
            </Button>
          )}
        </div>
      )}
      {error && <p role="alert" className="text-destructive">{error}</p>}
    </div>
  )
}
